import { shortMonthAndDay, TextCell } from "@similarweb/pro-logic-components";

import {
    CHANGE_DAY_COLUMN_PREFIX,
    FIRST_DAY_COLUMN_PREFIX,
    LAST_DAY_COLUMN_PREFIX,
    UNDEFINED_SIGN,
} from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import type { ESerpTypes } from "app/common/serp/SerpFilterMaps";
import { NewAndLostChangeCell } from "app/components/React/Table/cells/NewAndLostChangeCell";
import { CellHeaderRightAlignedWithLegend } from "app/components/React/Table/headerCells/CellHeaderRightAlignedWithLegend";
import { translate } from "app/filters/i18nFilter";
import { EGranularities } from "app/pages/app-intelligence/constants";
import { getDisplayNameForFirstLastColumns } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportDashboardTableColumnsConfig";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { FlexRowFlexEnd } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { getTableMetrics } from "./KeywordsCamapaignKeywordsReportTableUtils";
import type { IKeywordsReportTableMetrics, ISelectedSites } from "./KeywordsReportTypes";
import { EKeywordsReportKeywordsTableMetrics } from "./KeywordsReportTypes";
import { getPositionMetricTooltip } from "./utils/positionTooltipCopy";

const METRICS_MIN_WIDTH_SINGLE = 118;
const METRICS_MIN_WIDTH_COMPARE = 108;
const getChangeColumnHeader = () => translate("analysis.source.search.all.table.columns.change.title");

const getChangeFirstDayColumnConfig = (
    selectedSite: ISelectedSites,
    durationObject: DatesRange,
    granularity: GranularityNames,
    isSingleMode: boolean,
    siteColor: string,
    tabConfig: IKeywordsReportTableMetrics,
    pickerGroupName: string,
    tab: EKeywordsReportKeywordsTableMetrics,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    return {
        field: `${FIRST_DAY_COLUMN_PREFIX}${tabConfig.metricID}${selectedSite.id}`,
        cellComponent: (props) => {
            return (
                <FlexRowFlexEnd>
                    <TextCell> {props.value} </TextCell>
                </FlexRowFlexEnd>
            );
        },
        headerCellClass: "no-padding-left",
        headerComponent: CellHeaderRightAlignedWithLegend,
        format: "minusNAFilter",
        displayName: getDisplayNameForFirstLastColumns(durationObject, granularity, true, isSingleMode, tab),
        tooltip: getPositionMetricTooltip(
            tabConfig,
            translate(tabConfig?.firstLastDateTooltipKey),
            positionExcludedSerpFeatures,
        ),
        bulletColor: siteColor,
        minWidth: isSingleMode ? METRICS_MIN_WIDTH_SINGLE : METRICS_MIN_WIDTH_COMPARE,
        width: isSingleMode ? METRICS_MIN_WIDTH_SINGLE : METRICS_MIN_WIDTH_COMPARE,
        title: durationObject?.from.format(shortMonthAndDay), //required for dashboard
        sortable: true,
        sortDirection: "desc",
        groupName: pickerGroupName,
    };
};

const getChangeLastDayColumnConfig = (
    selectedSite: ISelectedSites,
    durationObject: DatesRange,
    isSingleMode: boolean,
    siteColor: string,
    tabConfig: IKeywordsReportTableMetrics,
    pickerGroupName: string,
    displayName: string,
    dateTooltip: string = null,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    const column = {
        field: `${LAST_DAY_COLUMN_PREFIX}${tabConfig.metricID}${selectedSite?.id}`,
        cellComponent: (props) => {
            return (
                <FlexRowFlexEnd>
                    <TextCell> {props.value} </TextCell>
                </FlexRowFlexEnd>
            );
        },
        headerComponent: CellHeaderRightAlignedWithLegend,
        headerCellClass: "no-padding-left",
        format: "minusNAFilter",
        displayName,
        tooltip: getPositionMetricTooltip(
            tabConfig,
            dateTooltip ? dateTooltip : translate(tabConfig?.firstLastDateTooltipKey),
            positionExcludedSerpFeatures,
        ),
        bulletColor: siteColor,
        minWidth: isSingleMode ? METRICS_MIN_WIDTH_SINGLE : METRICS_MIN_WIDTH_COMPARE,
        width: isSingleMode ? METRICS_MIN_WIDTH_SINGLE : METRICS_MIN_WIDTH_COMPARE,
        sortable: true,
        sortDirection: "desc",
        title: durationObject?.to.format(shortMonthAndDay), //required for dashboard
        groupName: pickerGroupName,
    };
    return column;
};

const getChangeChangeDayColumnConfig = (
    selectedSite: ISelectedSites,
    isSingleMode: boolean,
    siteColor: string,
    tabConfig: IKeywordsReportTableMetrics,
    pickerGroupName: string,
) => {
    const column = {
        field: `${CHANGE_DAY_COLUMN_PREFIX}${tabConfig.metricID}${selectedSite?.id}`,
        cellComponent: (props) => {
            return (
                <NewAndLostChangeCell
                    value={props.value}
                    undefinedSign={UNDEFINED_SIGN}
                    maxFromToValidValues={tabConfig.maxChangeValidValue}
                    isPercentage={tabConfig?.isChangePercentage}
                    cellFilter={tabConfig?.changeFilter ?? tabConfig?.metricFilter}
                />
            );
        },
        cellFilter: tabConfig?.changeFilter ?? tabConfig?.metricFilter,
        displayName: getChangeColumnHeader(),
        headerComponent: CellHeaderRightAlignedWithLegend,
        headerCellClass: "no-padding-left",
        tooltip: "seo.keywordsCampaign.wizard.addingKeywordsStep.table.column.change.tooltip",
        title: translate("rankTracker.keywordsReport.table.column.change.dashboard.title"), //required for dashboard
        bulletColor: siteColor,
        minWidth: isSingleMode ? METRICS_MIN_WIDTH_SINGLE : METRICS_MIN_WIDTH_COMPARE,
        width: isSingleMode ? METRICS_MIN_WIDTH_SINGLE : METRICS_MIN_WIDTH_COMPARE,
        sortable: true,
        sortDirection: "desc",
        groupName: pickerGroupName,
    };
    return column;
};

// Change Columns
export const getChangeColumnsSingleSiteSelected = (
    selectedSite: ISelectedSites,
    durationObject: DatesRange,
    granularity: GranularityNames,
    isSingleMode: boolean,
    tab: EKeywordsReportKeywordsTableMetrics,
    siteColor?: string,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    const tabConfig = getTableMetrics(EGranularities[granularity]).find((config) => config.metricID === tab);
    const isMoreThanOneDaySelected = durationObject?.to.diff(durationObject?.from, "days") > 0;
    const pickerGroupName = translate(tabConfig.columnPickerKey);

    const presentFirstDayColumn =
        isSingleMode && isMoreThanOneDaySelected && tab !== EKeywordsReportKeywordsTableMetrics.ALL;
    const firstDayColumn = getChangeFirstDayColumnConfig(
        selectedSite,
        durationObject,
        granularity,
        isSingleMode,
        siteColor,
        tabConfig,
        pickerGroupName,
        tab,
        positionExcludedSerpFeatures,
    );

    const dateToShow = getDisplayNameForFirstLastColumns(durationObject, granularity, false, isSingleMode, tab);
    const tableHeader = isSingleMode ? dateToShow : selectedSite?.id;
    const tableHeaderTooltip = isSingleMode ? null : dateToShow;

    const lastDayColumn = getChangeLastDayColumnConfig(
        selectedSite,
        durationObject,
        isSingleMode,
        siteColor,
        tabConfig,
        pickerGroupName,
        tableHeader,
        tableHeaderTooltip,
        positionExcludedSerpFeatures,
    );
    const changeColumn = getChangeChangeDayColumnConfig(
        selectedSite,
        isSingleMode,
        siteColor,
        tabConfig,
        pickerGroupName,
    );
    const changeColumns = [
        presentFirstDayColumn && firstDayColumn,
        lastDayColumn,
        isMoreThanOneDaySelected && changeColumn,
    ];
    return changeColumns.filter(Boolean);
};

export const getChangeColumnsMultipleSitesSelected = (
    selectedSites: ISelectedSites[],
    durationObject: DatesRange,
    granularity: GranularityNames,
    tab: EKeywordsReportKeywordsTableMetrics,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    return selectedSites.reduce((res, site) => {
        const changeColumnsForSingleSite = getChangeColumnsSingleSiteSelected(
            site,
            durationObject,
            granularity,
            false,
            tab,
            site.color,
            positionExcludedSerpFeatures,
        );
        return [...res, ...changeColumnsForSingleSite];
    }, []);
};

const getTabAllSelected = (
    selectedSite: ISelectedSites,
    durationObject: DatesRange,
    granularity: GranularityNames,
    isSingleMode: boolean,
    siteColor?: string,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    const tableMetrics = getTableMetrics(EGranularities[granularity]);
    const tabsConfig = tableMetrics.filter((config) => config.metricID !== EKeywordsReportKeywordsTableMetrics.ALL);
    const columnPickerKey = tableMetrics.find(
        (metric) => metric.metricID === EKeywordsReportKeywordsTableMetrics.ALL,
    )?.columnPickerKey;
    const pickerGroupName = translate(columnPickerKey);

    const columns = tabsConfig.reduce((acc, tab) => {
        const lastDayHeader = getDisplayNameForFirstLastColumns(
            durationObject,
            granularity,
            false,
            isSingleMode,
            tab.metricID,
        );
        const lastDayColumn = getChangeLastDayColumnConfig(
            selectedSite,
            durationObject,
            isSingleMode,
            siteColor,
            tab,
            pickerGroupName,
            lastDayHeader,
            null,
            positionExcludedSerpFeatures,
        );
        const changeColumn = getChangeChangeDayColumnConfig(
            selectedSite,
            isSingleMode,
            siteColor,
            tab,
            pickerGroupName,
        );
        acc.push(lastDayColumn, changeColumn);
        return acc;
    }, []);

    return columns;
};

export const getChangeColumns = (
    selectedSites: ISelectedSites[],
    durationObject: DatesRange,
    granularity: GranularityNames,
    tab: EKeywordsReportKeywordsTableMetrics,
    positionExcludedSerpFeatures?: readonly ESerpTypes[] | null,
) => {
    if (tab === EKeywordsReportKeywordsTableMetrics.ALL) {
        return getTabAllSelected(
            selectedSites[0],
            durationObject,
            granularity,
            true,
            undefined,
            positionExcludedSerpFeatures,
        );
    }
    const changeColumns =
        selectedSites?.length === 1
            ? getChangeColumnsSingleSiteSelected(
                  selectedSites[0],
                  durationObject,
                  granularity,
                  true,
                  tab,
                  undefined,
                  positionExcludedSerpFeatures,
              )
            : getChangeColumnsMultipleSitesSelected(
                  selectedSites,
                  durationObject,
                  granularity,
                  tab,
                  positionExcludedSerpFeatures,
              );
    return changeColumns;
};
